import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import CreatableSelect from "react-select/creatable";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { errorToasterMessage } from "@/utils/functions/toaster";
import { AiConfigurationSkeleton } from "@/components/skeletons";
import ConfirmationModal from "@/components/modals/ConfirmationModal";
import { AI_PROVIDER } from "@/utils/constants/constants";
import { SettingCheckboxCard } from "@/components/cards/SettingCheckboxCard";
import { BUTTONS, HEADINGS } from "@/utils/constants/appConstants/commonConstants";
import { getAiConfigurationById, getLLMProviderList, patchAiConfiguration, revokeAiConfiguration } from "@/services/setting.service";
import StatusToggle from "@/components/toggles/StatusToggle";
import { Button } from "@/components/ui/button";

interface AiModelOption {
    label: string;
    value: string;
}

interface AiConfigurations {
    provider?: string;
    models?: AiModelOption[];
    encrypted_api_key?: string;
    is_active?: boolean;
    is_default?: boolean;
    meta_data?: {
        deployment_name?: string;
        endpoint?: string;
        api_version?: string;
        monthly_quota?: number;
        tokens_used?: number;
        allow_messages_limit?: boolean;
        messages_limit?: number;
    }
}

const AiConfigurations = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [patchLoading, setPatchLoading] = useState(false);
    const [aiConfigurations, setAiConfigurations] = useState<AiConfigurations | null>(null);
    const [loading, setLoading] = useState(true);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [llmProviderList, setLlmProviderList] = useState<Record<string, unknown[]>>({});

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;
        switch (id) {
            case "api-key": setAiConfigurations({ ...aiConfigurations, encrypted_api_key: value }); break;
            case "monthly-qouta": setAiConfigurations({ ...aiConfigurations, meta_data: { ...aiConfigurations?.meta_data, monthly_quota: parseInt(value) } }); break;
            case "model": setAiConfigurations({
                ...aiConfigurations,
                models: value.split(',').map((item) => {
                    const trimmed = item.trim();
                    return { label: trimmed, value: trimmed };
                }),
            }); break;
            case "endpoint": setAiConfigurations({ ...aiConfigurations, meta_data: { ...aiConfigurations?.meta_data, endpoint: value } }); break;
            case "deployment": setAiConfigurations({ ...aiConfigurations, meta_data: { ...aiConfigurations?.meta_data, deployment_name: value } }); break;
            case "api-version": setAiConfigurations({ ...aiConfigurations, meta_data: { ...aiConfigurations?.meta_data, api_version: value } }); break;
            case "mark-default": setAiConfigurations({ ...aiConfigurations, is_default: e.target.checked }); break;
            case "is-active": setAiConfigurations({ ...aiConfigurations, is_active: e.target.checked }); break;
            case "allow-messages-limit": setAiConfigurations({ ...aiConfigurations, meta_data: { ...aiConfigurations?.meta_data, allow_messages_limit: e.target.checked } }); break;
            case "messages-limit": setAiConfigurations({ ...aiConfigurations, meta_data: { ...aiConfigurations?.meta_data, messages_limit: parseInt(value) } }); break;
        }
    };

    const modelOptions = useMemo(() => {
        const provider = aiConfigurations?.provider;
        if (!provider) return [];
        return (llmProviderList[provider] as Array<{ label: string; value: string }> | undefined) ?? [];
    }, [aiConfigurations?.provider, llmProviderList]);

    const modelSelectValue = useMemo(
        () => aiConfigurations?.models || [],
        [aiConfigurations?.models]
    );

    const handleModelsChange = (selectedOption: any) => {
        const selected = selectedOption
            ? Array.isArray(selectedOption)
                ? selectedOption
                : [selectedOption]
            : [];
        setAiConfigurations({
            ...aiConfigurations,
            models: selected.map((item: AiModelOption) => ({
                label: item.label,
                value: item.value,
            })),
        });
    };

    const handleRevoke = async () => {
        if (params.id) {
            setPatchLoading(true);
            await revokeAiConfiguration(params.id, () => {
                navigate("/manage-ai");
                toast.success(HEADINGS.CONFIGURATION_REVOKED_SUCCESSFULLY);
            }, (err: any) => console.log(err));
            setPatchLoading(false);
        }
    };

    const handleSave = async () => {
        if (!aiConfigurations?.encrypted_api_key || !aiConfigurations?.models || aiConfigurations.models.length === 0) {
            toast.error(HEADINGS.REQUIRED_FIELDS);
            return;
        }
        if (params.id) {
            setPatchLoading(true);
            await patchAiConfiguration(aiConfigurations, () => {
                toast.success(HEADINGS.CONFIGURATION_UPDATED_SUCCESSFULLY);
                navigate("/manage-ai");
            }, (err: any) => console.log(err));
            setPatchLoading(false);
        }
    };

    useEffect(() => {
        getLLMProviderList((response: Record<string, unknown[]>) => setLlmProviderList(response ?? {}));
    }, []);

    useLayoutEffect(() => {
        if (params.id) {
            setLoading(true);
            setAiConfigurations(null);
        }
    }, [params.id]);

    useEffect(() => {
        if (!params.id) {
            setLoading(false);
            setAiConfigurations(null);
            return;
        }

        setLoading(true);
        setAiConfigurations(null);

        getAiConfigurationById(params.id, (res: any) => {
            const transformedRes = {
                ...res,
                models: res.models?.map((model: any) => {
                    if (typeof model === "string") {
                        return { label: model, value: model };
                    }
                    return {
                        label: model.label || model.value || "",
                        value: model.value || model.label || "",
                    };
                }) || [],
            };
            setAiConfigurations(transformedRes);
            setLoading(false);
        }, (err: any) => {
            errorToasterMessage(err);
            setAiConfigurations(null);
            setLoading(false);
        });
    }, [params.id]);

    return (
        <div className="gs-ai-config-page">
            <h3 className="gs-ai-config-title" id="ai-config-title">
                {loading ? (
                    <Skeleton className="gs-ai-config-title-skeleton h-7 w-48 max-w-full rounded-[4px]" />
                ) : (
                    aiConfigurations?.provider
                )}
            </h3>

            <div className="gs-ai-config-card">
                {loading ? (
                    <AiConfigurationSkeleton />
                ) : aiConfigurations ? (
                    <div className="gs-ai-config-form">
                        <div className="gs-ai-config-field">
                            <label className="gs-ai-config-label" htmlFor="api-key">{HEADINGS.API_KEY}</label>
                            <Input onChange={handleChange} value={aiConfigurations.encrypted_api_key} type="text" id="api-key" />
                        </div>
                        <div className="gs-ai-config-field">
                            <label className="gs-ai-config-label" htmlFor="monthly-qouta">{HEADINGS.MONTHLY_USAGE_LIMIT}</label>
                            <Input onChange={handleChange} value={aiConfigurations.meta_data?.monthly_quota} id="monthly-qouta" />
                        </div>
                        <div className="gs-ai-config-field">
                            <label className="gs-ai-config-label" htmlFor="tokens_used">{HEADINGS.TOKENS_USED}</label>
                            <Input disabled type="number" value={aiConfigurations.meta_data?.tokens_used} id="tokens_used" />
                        </div>
                        <div className="gs-ai-config-field">
                            <label className="gs-ai-config-label" htmlFor="ai-config-models">
                                {HEADINGS.MODEL}{" "}
                                <a href="https://platform.openai.com/docs/models" target="_blank" rel="noopener noreferrer">{HEADINGS.DOCUMENTATION}</a>
                                {" "}{HEADINGS.FOR_MORE_INFORMATION}
                            </label>
                            <CreatableSelect
                                inputId="ai-config-models"
                                isMulti
                                options={modelOptions}
                                value={modelSelectValue}
                                onChange={handleModelsChange}
                                className="gs-upload-ai-model-select"
                                classNamePrefix="react-select"
                                isClearable
                                placeholder="Type a model name and press Enter"
                            />
                        </div>
                        <div className="gs-ai-config-field">
                            <StatusToggle
                                name="allow-messages-limit"
                                id="allow-messages-limit"
                                checked={aiConfigurations.meta_data?.allow_messages_limit ?? false}
                                onChange={handleChange}
                            >
                                {HEADINGS.ALLOW_MESSAGES_LIMIT}
                            </StatusToggle>
                        </div>
                        <div className="gs-ai-config-field">
                            <label className="gs-ai-config-label" htmlFor="messages-limit">{HEADINGS.MESSAGES_LIMIT}</label>
                            <Input
                                disabled={!aiConfigurations.meta_data?.allow_messages_limit}
                                onChange={handleChange}
                                value={aiConfigurations.meta_data?.messages_limit}
                                type="number"
                                id="messages-limit"
                            />
                        </div>
                        {aiConfigurations.provider === AI_PROVIDER.AZURE_OPENAI && (
                            <>
                                <div className="gs-ai-config-field">
                                    <label className="gs-ai-config-label" htmlFor="endpoint">{HEADINGS.ENDPOINT}</label>
                                    <Input onChange={handleChange} value={aiConfigurations.meta_data?.endpoint} type="text" id="endpoint" />
                                </div>
                                <div className="gs-ai-config-field">
                                    <label className="gs-ai-config-label" htmlFor="deployment">{HEADINGS.DEPLOYMENT}</label>
                                    <Input onChange={handleChange} value={aiConfigurations.meta_data?.deployment_name} type="text" id="deployment" />
                                </div>
                                <div className="gs-ai-config-field">
                                    <label className="gs-ai-config-label" htmlFor="api-version">{HEADINGS.API_VERSION}</label>
                                    <Input onChange={handleChange} value={aiConfigurations.meta_data?.api_version} type="text" id="api-version" />
                                </div>
                            </>
                        )}
                        <SettingCheckboxCard className="margin-class" id="mark-default" onChange={(e) => handleChange(e)} name="is_default" checked={aiConfigurations.is_default || false}>
                            <p id="setting_is_default_label">{HEADINGS.MARK_AS_DEFAULT}</p>
                        </SettingCheckboxCard>
                        <SettingCheckboxCard className="margin-class" id="is-active" onChange={(e) => handleChange(e)} name="is_active" checked={aiConfigurations.is_active || false}>
                            <p id="setting_is_active_label">{HEADINGS.MARK_AS_ACTIVE}</p>
                        </SettingCheckboxCard>

                        <div className="gs-ai-config-actions">
                            <Button variant="destructive" onClick={() => setShowConfirmationModal(true)}>
                                {BUTTONS.REVOKE} {HEADINGS.CONFIGURATION}
                            </Button>
                            <Button
                                variant="default"
                                className="bg-primary hover:bg-primary-hover-alt"
                                disabled={patchLoading}
                                onClick={handleSave}
                            >
                                {BUTTONS.SAVE}
                            </Button>
                        </div>

                        {showConfirmationModal && (
                            <ConfirmationModal
                                show={showConfirmationModal}
                                setShow={setShowConfirmationModal}
                                title={HEADINGS.REVOKE_CONFIRMATION_TITLE}
                                message={HEADINGS.REVOKE_CONFIRMATION_MESSAGE}
                                onConfirm={handleRevoke}
                            />
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default AiConfigurations;
